import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { notifyErr, notifyOk } from '@/lib/notify';
import { stockLabel } from '../../products/productListModel';
import {
    useUpdateCatalogAvailability,
    type CatalogAvailabilityPatch,
} from '../../catalog/hooks/useStoreCatalog';
import type { CatalogProductDetail, CatalogVariantRow } from '../../catalog/types';
import { availabilityLabel } from '../lib/catalogImageUrl';

const MANUAL_OPTIONS = [
    { value: '', label: 'Según el feed' },
    { value: 'in_stock', label: 'En stock' },
    { value: 'on_order', label: 'Bajo pedido' },
    { value: 'out_of_stock', label: 'Agotado' },
] as const;

type ManualCode = '' | 'in_stock' | 'on_order' | 'out_of_stock';

interface LineDraft {
    disponibilidadManual: ManualCode;
    leadTimeDays: string;
    units: string;
    stockEnforced: boolean;
}

function asManual(value: string | null | undefined): ManualCode {
    if (value === 'in_stock' || value === 'on_order' || value === 'out_of_stock') return value;
    return '';
}

function emptyLine(row?: Pick<CatalogVariantRow, 'disponibilidadManual' | 'leadTimeDaysManual' | 'stockQtyManual' | 'stockEnforced'> | CatalogProductDetail): LineDraft {
    return {
        disponibilidadManual: asManual(row?.disponibilidadManual),
        leadTimeDays: row?.leadTimeDaysManual == null ? '' : String(row.leadTimeDaysManual),
        units: row?.stockQtyManual == null ? '' : String(row.stockQtyManual),
        stockEnforced: row?.stockEnforced === true,
    };
}

function parseDays(raw: string): number | null {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isInteger(n) && n >= 0 && n <= 180 ? n : Number.NaN;
}

function parseUnits(raw: string): number | null {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isInteger(n) && n >= 0 ? n : Number.NaN;
}

function linePatch(draft: LineDraft): Omit<CatalogAvailabilityPatch, 'variants'> {
    return {
        disponibilidad_manual: draft.disponibilidadManual || null,
        lead_time_days_manual: draft.disponibilidadManual === 'on_order' ? parseDays(draft.leadTimeDays) : null,
        stock_qty_manual: parseUnits(draft.units),
        stock_enforced: draft.stockEnforced,
    };
}

function LineFields({
    idPrefix,
    draft,
    onChange,
    feedCode,
    feedText,
    effective,
    discrepancy,
}: {
    idPrefix: string;
    draft: LineDraft;
    onChange: (next: LineDraft) => void;
    feedCode: string;
    feedText?: string | null;
    effective?: string;
    discrepancy?: boolean;
}) {
    return (
        <div className="space-y-2">
            <div className="space-y-1">
                <Label htmlFor={`${idPrefix}-manual`}>Disponibilidad</Label>
                <select
                    id={`${idPrefix}-manual`}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={draft.disponibilidadManual}
                    onChange={(event) => onChange({ ...draft, disponibilidadManual: event.target.value as ManualCode })}
                >
                    {MANUAL_OPTIONS.map((option) => (
                        <option key={option.value || 'feed'} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </div>
            {draft.disponibilidadManual === 'on_order' ? (
                <div className="space-y-1">
                    <Label htmlFor={`${idPrefix}-days`}>Plazo (días)</Label>
                    <Input
                        id={`${idPrefix}-days`}
                        inputMode="numeric"
                        value={draft.leadTimeDays}
                        onChange={(event) => onChange({ ...draft, leadTimeDays: event.target.value })}
                        placeholder="10"
                    />
                </div>
            ) : null}
            <label className="flex items-center gap-2 text-sm">
                <input
                    type="checkbox"
                    checked={draft.stockEnforced}
                    onChange={(event) => onChange({ ...draft, stockEnforced: event.target.checked })}
                />
                Control de stock
            </label>
            {draft.stockEnforced ? (
                <div className="space-y-1">
                    <Label htmlFor={`${idPrefix}-units`}>Unidades</Label>
                    <Input
                        id={`${idPrefix}-units`}
                        inputMode="numeric"
                        value={draft.units}
                        onChange={(event) => onChange({ ...draft, units: event.target.value })}
                    />
                </div>
            ) : null}
            <p className="text-xs text-muted-foreground">
                Feed: {stockLabel(feedCode, null)}
                {feedText ? ` · ${availabilityLabel(null, feedText)}` : ''}
            </p>
            <p className="text-sm">Publicado: {stockLabel(effective || feedCode, null)}</p>
            {discrepancy ? (
                <p className="text-xs text-amber-700">El feed dice {stockLabel(feedCode, null)}. La sync no revertirá el valor manual.</p>
            ) : null}
        </div>
    );
}

export function CatalogAvailabilityEditor({ detail }: { detail: CatalogProductDetail }) {
    const save = useUpdateCatalogAvailability();
    const [product, setProduct] = useState<LineDraft>(() => emptyLine(detail));
    const [variants, setVariants] = useState<Record<number, LineDraft>>({});

    useEffect(() => {
        setProduct(emptyLine(detail));
        const next: Record<number, LineDraft> = {};
        for (const variant of detail.variants ?? []) {
            next[variant.id] = emptyLine(variant);
        }
        setVariants(next);
    }, [detail]);

    function submit() {
        const productPatch = linePatch(product);
        if (Number.isNaN(productPatch.lead_time_days_manual as number) || Number.isNaN(productPatch.stock_qty_manual as number)) {
            notifyErr('Revisa el plazo o las unidades');
            return;
        }
        if (product.stockEnforced && productPatch.stock_qty_manual == null) {
            notifyErr('Indica las unidades antes de activar el control de stock');
            return;
        }
        const variantPatches: NonNullable<CatalogAvailabilityPatch['variants']> = [];
        for (const variant of detail.variants ?? []) {
            const draft = variants[variant.id] ?? emptyLine(variant);
            const patch = linePatch(draft);
            if (Number.isNaN(patch.lead_time_days_manual as number) || Number.isNaN(patch.stock_qty_manual as number)) {
                notifyErr('Revisa el plazo o las unidades de la variante');
                return;
            }
            if (draft.stockEnforced && patch.stock_qty_manual == null) {
                notifyErr('Indica las unidades de la variante antes de activar el control de stock');
                return;
            }
            variantPatches.push({ id: variant.id, ...patch });
        }
        save.mutate(
            { productId: detail.id, patch: { ...productPatch, variants: variantPatches } },
            {
                onSuccess: () => notifyOk('Disponibilidad guardada'),
                onError: (error: unknown) => notifyErr(error instanceof Error ? error.message : 'No se ha podido guardar'),
            },
        );
    }

    return (
        <section className="space-y-3">
            <h2 className="text-sm font-semibold">Disponibilidad</h2>
            <LineFields
                idPrefix="av-product"
                draft={product}
                onChange={setProduct}
                feedCode={detail.disponibilidad}
                feedText={detail.disponibilidadTexto}
                effective={detail.disponibilidadEfectiva}
                discrepancy={detail.feedDiscrepancy}
            />
            {(detail.variants ?? []).map((variant) => (
                <div key={variant.id} className="space-y-2 rounded-lg border p-2">
                    <p className="font-mono text-xs">{variant.sku || variant.externalId || variant.id}</p>
                    <LineFields
                        idPrefix={`av-${variant.id}`}
                        draft={variants[variant.id] ?? emptyLine(variant)}
                        onChange={(next) => setVariants((prev) => ({ ...prev, [variant.id]: next }))}
                        feedCode={variant.disponibilidad}
                        effective={variant.disponibilidadEfectiva}
                        discrepancy={variant.feedDiscrepancy}
                    />
                </div>
            ))}
            <Button type="button" size="sm" onClick={submit} disabled={save.isPending}>
                {save.isPending ? 'Guardando…' : 'Guardar disponibilidad'}
            </Button>
        </section>
    );
}
